import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';

import type { Backlog, IssueCard } from '@tasks/contracts';

import { queryKeys } from '../../api/queryKeys';
import { addIssuesToSprint, removeIssuesFromSprint } from '../../api/sprints';
import { useToast } from '../../components/Toast';
import { useFloatingStore } from '../../stores/floatingStore';
import { humanizeError } from '../../lib/errors';
import { moveIssueBetweenGroups } from './backlogModel';

export type BacklogDnd = {
  readonly activeCard: IssueCard | null;
  readonly sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
};

const BACKLOG_CONTAINER = 'backlog';

/** DnD бэклога: между бэклогом и спринтами, оптимистично (UC-SPR-03). */
export function useBacklogDnd(projectKey: string, backlog: Backlog | null): BacklogDnd {
  const [activeCard, setActiveCard] = useState<IssueCard | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const beginDrag = useFloatingStore((state) => state.beginDrag);
  const endDrag = useFloatingStore((state) => state.endDrag);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const addMutation = useMutation({
    mutationFn: (variables: { readonly sprintId: string; readonly issueId: string }) =>
      addIssuesToSprint(variables.sprintId, { issueIds: [variables.issueId] }),
    onError: (cause: unknown, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
      void variables;
      toast.error(humanizeError(cause).message, { code: humanizeError(cause).code });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (variables: { readonly sprintId: string; readonly issueId: string }) =>
      removeIssuesFromSprint(variables.sprintId, { issueIds: [variables.issueId] }),
    onError: (cause: unknown) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
      toast.error(humanizeError(cause).message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(projectKey) });
    },
  });

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const card = event.active.data.current?.['card'] as IssueCard | undefined;
      if (card) {
        setActiveCard(card);
        beginDrag();
      }
    },
    [beginDrag],
  );

  const handleDragCancel = useCallback(() => {
    setActiveCard(null);
    endDrag();
  }, [endDrag]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveCard(null);
      endDrag();
      const { active, over } = event;
      if (!over || !backlog) {
        return;
      }
      const card = active.data.current?.['card'] as IssueCard | undefined;
      const targetSprintId = over.data.current?.['sprintId'] as string | null | undefined;
      if (!card || targetSprintId === undefined) {
        return;
      }
      const target = targetSprintId === BACKLOG_CONTAINER ? null : targetSprintId;
      if (target === card.sprintId) {
        return;
      }

      // Оптимистично переносим карточку между контейнерами.
      // Обновляем все загруженные страницы бэклога (их ключи — потомки корня).
      queryClient.setQueriesData<Backlog>(
        { queryKey: queryKeys.backlog.view(projectKey) },
        (previous) => (previous ? moveIssueBetweenGroups(previous, card.id, target) : previous),
      );

      if (target === null) {
        if (card.sprintId) {
          removeMutation.mutate({ sprintId: card.sprintId, issueId: card.id });
        }
      } else {
        addMutation.mutate({ sprintId: target, issueId: card.id });
      }
    },
    [addMutation, backlog, endDrag, projectKey, queryClient, removeMutation],
  );

  return { activeCard, sensors, handleDragStart, handleDragEnd, handleDragCancel };
}

export { DndContext, DragOverlay, closestCorners };
